import { Report, ReportType } from '@/api/generatedApi';
import { CreateReportModal } from '@/components/Report/CreateReportModal';
import { REPORT_TITLES } from '@/components/Report/consts';
import { DeleteModal } from '@/components/modals/DeleteModal';
import { BulkDeleteReportsModal } from '@/components/modals/BulkDeleteReportsModal';
import { useFetchListReports } from '@/hooks/api/reports/useFetchListReports';
import { useDeleteReport } from '@/hooks/api/reports/useDeleteReport';
import { Box, styled, Typography } from '@mui/material';
import { useState } from 'react';
import { GenerateReportsSection } from '@/components/Report/GenerateReportsSection';
import { ReportsTable } from '@/components/Report/ReportsTable';
import { useTenants } from '@/services/tenants/useTenants';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';

const MainContentArea = styled(Box)({
  flex: 1,
  display: 'flex',
  height: '100%',
  overflow: 'hidden',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  width: '100%',
  overflowY: 'auto',
  padding: '24px',
});

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '1.25rem',
  fontWeight: 600,
  marginBottom: '16px',
  marginTop: '32px',
  color: theme.palette.primary.main,
}));

export const ReportPage = () => {
  usePageTitle('Reports');
  const { fetchData } = useFetchListReports();
  const { getPath } = useTenants();
  const navigate = useNavigate();

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedReportType, setSelectedReportType] = useState<ReportType>(ReportType.DUPLICATION);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [reportsToDelete, setReportsToDelete] = useState<Report[]>([]);
  const [hasReports, setHasReports] = useState(true);

  const fetchRows = async ({
    page,
    perPage,
  }: {
    page: number;
    perPage: number;
  }): Promise<{ reports: Report[] } & { pageData: { total: number } }> => {
    const response = await fetchData({ page, perPage });
    if (page === 1 && response.pageData.total > 0) {
      setHasReports(true);
    } else if (page === 1 && response.pageData.total === 0) {
      setHasReports(false);
    }
    return response;
  };

  const handleOpenModal = (reportType: ReportType) => {
    setSelectedReportType(reportType);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleReportCreated = (reportId: string) => {
    setHasReports(true);
    navigate(getPath(`report/${reportId}`));
  };

  const handleDeleteClick = (reports: Report[]) => {
    setReportsToDelete(reports);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setReportsToDelete([]);
  };

  const isBulkDelete = reportsToDelete.length > 1;
  const singleReport = reportsToDelete.length === 1 ? reportsToDelete[0] : undefined;

  return (
    <MainContentArea>
      <GenerateReportsSection onSelectReportType={handleOpenModal} />
      {hasReports && (
        <>
          <SectionTitle>Previous Reports</SectionTitle>
          <ReportsTable fetchRows={fetchRows} onDeleteClick={handleDeleteClick} />
        </>
      )}
      <CreateReportModal
        key={selectedReportType}
        isOpen={modalOpen}
        onClose={handleCloseModal}
        reportType={selectedReportType}
        onSuccess={handleReportCreated}
      />
      {deleteModalOpen && isBulkDelete && (
        <BulkDeleteReportsModal
          open={deleteModalOpen}
          onClose={handleCloseDeleteModal}
          selectedReports={reportsToDelete}
          onDeleteComplete={handleCloseDeleteModal}
        />
      )}
      {deleteModalOpen && singleReport && (
        <DeleteModal
          useHook={useDeleteReport}
          mutateParams={singleReport.id}
          isOpen={deleteModalOpen}
          onClose={handleCloseDeleteModal}
          title='Delete Report'
          itemName={singleReport.name || REPORT_TITLES[singleReport.reportType]}
          item={singleReport}
        />
      )}
    </MainContentArea>
  );
};
